import type { ItemFieldRow } from './item-field-reader';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonFloat } from './php-decimal-values';
import { integerCompileWorkingRow, integerCompileResult } from './integer-compute-slots';

const paths: Readonly<Record<string,string>> = {
    user:'images/user/avatars/',
    player_character:'images/user/player_characters/',
    player_character_picture:'images/user/player_characters/',
    player_character_model_capture:'images/user/player_characters/',
    campaign_header:'images/user/campaign_headers/',
    timeline_event_picture:'images/user/timelines/timeline_event_pictures/',
    article_picture:'images/user/articles/article_pictures/',
    article_banner:'images/user/articles/article_banners/',
    article_symbol:'images/user/articles/article_symbols/',
    source_header:'images/user/sources/source_headers/',
    source_header_picture:'images/user/sources/source_headers/',
    adventure_header:'images/user/adventures/adventure_headers/',
    encounter_location_picture:'images/user/encounters/location_pictures/',
    encounter_picture:'images/user/encounters/encounter_pictures/',
    location_picture:'images/user/locations/location_pictures/',
    location_map:'images/user/locations/location_maps/',
    encounter_creature_picture:'images/user/encounter_creatures/encounter_creature_pictures/',
    mesh_thumbnail:'images/meshes/mesh_thumbnails/',
    inventory_picture:'images/user/inventory/inventory_pictures/',
};
const fields = ['picture_i','picture_t','picture_t_updated','picture_private','picture_deleted','picture_created_by_id',
    'picture_updated_by_id','picture_id','picture_name','picture_description','primary_picture','picture_extension',
    'picture_width','picture_height','picture_thumbnail','picture_number','picture_attached_item_type',
    'picture_reference_attached_item_type','picture_attached_id'] as const;
const gridFields = ['map_grid_shape','map_grid_color','map_grid_hex_size','map_grid_width','map_grid_opacity',
    'map_grid_offset_x','map_grid_offset_y','map_grid_columns','map_grid_rows'] as const;
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
function scalarType(value: unknown): string {
    if (value == null) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    const token = JSON.stringify(value);
    if (/^-?\d+$/.test(token)) {
        const integer = BigInt(token);
        if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return 'int';
    }
    return 'float';
}
export interface PictureRowOptions {
    cdnUrl: string;
    auditContext?: unknown;
    keepAttachedId?: unknown;
    warning?: (kind: string,detail?: string) => void;
}
/** Actual raw picture-row projection. No SQL, CDN requests, permission actions or
 * global configuration reads. Lean display rows cannot reconstruct omitted inputs.
 */
export function compilePictureRow(input: unknown,options: PictureRowOptions): ItemFieldRow {
    if (typeof options.cdnUrl !== 'string') throw new TypeError('Explicit CDN URL must be a string');
    const read = (key: string): unknown => {
        if (typeof input === 'string') throw new TypeError('Cannot access column offset on string');
        if (!arrayLike(input)) { options.warning?.('invalid-offset-read',scalarType(input)); return null; }
        if (!Object.prototype.propertyIsEnumerable.call(input,key)) { options.warning?.('missing',key); return null; }
        return (input as Readonly<Record<string,unknown>>)[key];
    };
    const string = (value: unknown): string => {
        if (arrayLike(value)) options.warning?.('array-to-string');
        return phpJsonString(value);
    };
    const path = (key: unknown): string | undefined => {
        if (arrayLike(key)) throw new TypeError('Illegal picture path-map offset');
        const name = key == null ? '' : typeof key === 'number' || typeof key === 'boolean' ? String(phpJsonInteger(key)) : String(key);
        return Object.prototype.hasOwnProperty.call(paths,name) ? paths[name] : undefined;
    };
    // The first source lookup populates an unused $paths variable, but its read
    // warnings/errors still precede the actual reference-first URL lookup.
    path(read('picture_attached_item_type'));
    const prefix = path(read('picture_reference_attached_item_type')) ?? path(read('picture_attached_item_type')) ?? 'images/items/_base/';
    const url = options.cdnUrl + prefix + string(read('picture_id')) + '.' + string(read('picture_extension'));
    const out: Record<string,unknown> = {};
    for (const key of fields) out[key] = read(key);
    out.picture_url = url;
    for (const key of gridFields) out[key] = read(key);
    if (!phpJsonTruthy(options.auditContext)) {
        for (const key of ['picture_i','picture_t','picture_t_updated','picture_updated_by_id','picture_extension']) delete out[key];
        for (const key of ['picture_name','picture_description']) if (string(out[key] ?? '') === '') delete out[key];
        for (const key of ['picture_private','picture_deleted','primary_picture','picture_thumbnail'])
            if (phpJsonInteger(out[key] ?? 0) === 0n) delete out[key];
        const width = phpJsonInteger(out.picture_width ?? 0),height = phpJsonInteger(out.picture_height ?? 0);
        if (out.picture_attached_item_type !== 'location_map' && width > 0n && width === height) {
            delete out.picture_width; delete out.picture_height;
        }
        if (string(out.picture_attached_item_type ?? '') === 'item') delete out.picture_attached_item_type;
        if (!phpJsonTruthy(options.keepAttachedId)) delete out.picture_attached_id;
        for (const key of ['picture_reference_attached_item_type','map_grid_shape','map_grid_color'])
            if (string(out[key] ?? '') === '') delete out[key];
        for (const key of ['map_grid_hex_size','map_grid_width','map_grid_opacity','map_grid_offset_x','map_grid_offset_y'])
            if (phpJsonFloat(out[key] ?? 0) === 0) delete out[key];
        for (const key of ['map_grid_columns','map_grid_rows']) if (phpJsonInteger(out[key] ?? 0) === 0n) delete out[key];
    }
    return integerCompileResult(integerCompileWorkingRow(out));
}
